import Badge from '@/common/components/Badge';
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { CopyButton } from "@/common/components/CopyButton";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import LogRequestsTable from "@/common/components/LogRequestsTable";
import ResumeBox from '@/common/components/ResumeBox';
import Select from "@/common/components/Select";
import { ShowHeader } from "@/common/components/ShowHeader";
import Tabs from "@/common/components/Tabs";
import TextField from "@/common/components/TextField";
import { UnlockUserAccess } from "@/common/components/UnlockUserAccess";
import { userStatusTranslation } from "@/common/constants/userStatus.constant";
import { useCardWithSecureInformation } from "@/common/hooks/queries/useCardWithSecureInformation";
import useCompany from "@/common/hooks/queries/useCompany";
import useCompanyUser from "@/common/hooks/queries/useCompanyUser";
import { useCopyButton } from "@/common/hooks/useCopyButton";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { dateTransformer } from "@/common/utils/dateTransformer";
import { formatCpf, formatPhoneNumber } from "@/common/utils/formatters";
import { cardStatusTranslation } from '@/features/cards/constants/cardStatusTranslation.constant';
import { CreateCompanyUserCard } from "@/features/privateCompanies/components/companyUserForm/CreateCompanyUserCard";
import RequestConsumptionPasswordReset from "@/features/privateCompanies/components/RequestConsumptionPasswordReset";
import RequestPasswordReset from "@/features/privateCompanies/components/RequestPasswordReset";
import ShowConsumptionPassword from "@/features/privateCompanies/components/ShowConsumptionPassword";
import BlockPersonalCard from '@/features/privateCompanies/components/showUser/BlockPersonalCard';
import ClientUserTokens from "@/features/privateCompanies/components/showUser/clientUserTokens";
import { ConsumptionHistory } from "@/features/privateCompanies/components/showUser/consumptionHistory";
import { ConsumptionLimit } from "@/features/privateCompanies/components/showUser/ConsumptionLimit";
import UnblockPersonalCard from '@/features/privateCompanies/components/showUser/UnblockPersonalCard';
import UserBranchesAccesses from "@/features/privateCompanies/components/showUser/userBranchesAccesses";
import { useQueryClient } from "@tanstack/react-query";
import { UserRoundIcon } from "lucide-react";
import React from 'react';
import { Link, useParams } from "react-router-dom";
import ResetDriverAppPin from "../../../components/resetDriverAppPin";
import { useGetCurrentTab } from '@/common/hooks/useGetCurrentTab';

const Show = () => {
  const { companyId, id } = useParams();
  const queryClient = useQueryClient();

  const { control, copySuccess, copyFailed } = useCopyButton();

  const {
    data: company,
    isPending: companyIsPending,
  } = useCompany({ id: companyId || '' });

  const {
    data: companyUser,
    isPending: companyUserIsPending,
    error: companyUserError = {} as any,
    refetch: refetchCompanyUser,
  } = useCompanyUser({ id: id || '' });

  const {
    mutate: personalCard,
    isPending: personalCardIsPending,
  } = useCardWithSecureInformation({
    onSuccess: (response) => copySuccess(response?.data?.data?.number),
    onError: copyFailed
  });

  const { hasUserPermission } = useUserPermissions();

  const userHasPermissionToResetDriverAppPin = hasUserPermission('Admin::Client::Users::ResetDriverAppPin');
  const userHasPermissionToRequestPasswordReset = hasUserPermission('Admin::Client::Users::RequestPasswordReset');
  const userHasPermissionToShowConsumptionPassword = hasUserPermission('Admin::Client::Users::ShowConsumptionPassword');
  const userHasPermissionToRequestConsumptionPasswordReset = hasUserPermission('Admin::Client::Users::RequestConsumptionPasswordReset');

  const currentTab = useGetCurrentTab(`/companies/${companyId}/users/${id}`);

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={companyUserError?.status}
      refetchFunction={refetchCompanyUser}
      notFoundFallback={`/companies/${companyId}/users`}
      notFoundButtonText='Ir para Usuários'
    >
      <div>
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/companies/${companyId}/users`,
            searchLastRouteFromReference: `/companies/${companyId}/users/${id}`,
          }}
          isLoading={companyIsPending || companyUserIsPending}
          titleClassName="font-normal text-md text-white-400"
          title={company?.general_information?.fantasy_name}
          subtitle={companyUser?.full_name}
          buttons={!currentTab && (
            <Link to={`/companies/${companyId}/users/${id}/edit`}>
              <Button
                name="access_company_user_update"
                className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                operationKlassToCheckPermission="Admin::Client::Users::Update"
              >
                Editar
              </Button>
            </Link>
          )}
        />

        <Tabs
          tabsListClassName="w-full bg-transparent rounded-none"
          tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
          tabsContentClassName="mt-6"
          isLoading={companyIsPending}
          skeletonsCount={4}
          tabsList={[
            {
              label: 'Dados',
              value: '',
              body: (
                <>
                  <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-192px)]">
                    {!!companyUser?.failed_attempts && companyUser?.failed_attempts > 0 && (
                      <UnlockUserAccess
                        userId={id || ''}
                        failedAttempts={companyUser?.failed_attempts}
                        lockedAt={companyUser?.locked_at}
                        entity="client"
                        operationKlassToCheckPermission="Admin::Client::Users::UnlockUserAccess"
                        onSuccess={() => {
                          queryClient.invalidateQueries({ queryKey: ['companyUser', id] })
                        }}
                      />
                    )}

                    <p className="text-lg font-bold">Dados pessoais</p>
                    <div className="flex flex-col gap-2">
                      <p className="text-xs">Foto</p>
                      <ImageWithPlaceholder
                        url={companyUser?.profile_picture?.url}
                        alt={companyUser?.full_name}
                        className="size-20"
                        iconClassName="size-16"
                        placeholderIcon={UserRoundIcon}
                      />
                    </div>
                    <div className="flex gap-2 w-full justify-between">
                      <TextField
                        disabled
                        label="CPF"
                        className="w-3/4"
                        value={companyUser?.cpf ? formatCpf(companyUser.cpf) : ''}
                        data-testid="cpf"
                      />
                      <TextField
                        disabled
                        label="Data de nascimento"
                        className="w-1/4"
                        value={companyUser?.date_of_birth ? dateTransformer(companyUser?.date_of_birth) : ''}
                        data-testid="date_of_birth"
                      />
                    </div>
                    <div className="flex gap-2 w-full justify-between">
                      <TextField
                        disabled
                        label="Nome"
                        className="w-3/4"
                        value={companyUser?.full_name || ''}
                        data-testid="full_name"
                      />
                      <Select
                        label="Perfil"
                        className="w-1/4"
                        value={companyUser?.user_roles?.map(({ role }) => ({
                          label: role.display_name,
                          value: role.id,
                        }))}
                        isMulti
                        isDisabled
                        data-testid="roles"
                      />
                    </div>
                    <TextField
                      disabled
                      label="Status"
                      value={userStatusTranslation[(companyUser?.status || 'default') as keyof typeof userStatusTranslation]}
                      data-testid="status"
                    />
                    <TextField
                      disabled
                      label="Matrícula"
                      value={companyUser?.enrollment_number || ''}
                      data-testid="enrollment_number"
                    />
                    <TextField
                      disabled
                      label="Filial"
                      value={companyUser?.branch?.display_name || ''}
                      data-testid="branch_id"
                    />
                    <TextField
                      disabled
                      label="Telefone"
                      value={companyUser?.phone?.number ? formatPhoneNumber(companyUser.phone.number) : ''}
                      data-testid="phone_number"
                    />
                    <TextField
                      disabled
                      label="Email"
                      value={companyUser?.email || ''}
                      data-testid="email"
                    />
                    <div className="flex gap-2 w-full justify-between">
                      <TextField
                        disabled
                        label="Número da CNH"
                        className="grow"
                        value={companyUser?.current_driver_license_registration_number || ''}
                        name="current_driver_license_registration_number"
                      />

                      <Select
                        label="Categorias da CNH"
                        placeholder=""
                        className="grow"
                        value={companyUser?.user_driver_license_categories?.map(({ license_category }) => ({
                          label: license_category.display_name,
                          value: license_category.id,
                        }))}
                        isMulti
                        isDisabled
                        data-testid="user_driver_license_categories"
                      />

                      <TextField
                        disabled
                        label="Data de vencimento"
                        className="grow"
                        value={companyUser?.current_driver_license_expiration_date ? dateTransformer(companyUser?.current_driver_license_expiration_date) : ''}
                        name="current_driver_license_expiration_date"
                      />
                    </div>

                    <TextField
                      disabled
                      label="Nome que será impresso no cartão"
                      value={companyUser?.name_on_card || ''}
                      name="name_on_card"
                    />

                    <div className="w-full rounded-sm p-4 bg-white-100">
                      {!companyUser?.cards.length ? (
                        <div className="flex w-full items-center justify-between">
                          <p>Cartão personal</p>
                          {company?.kind !== "public_company" && (
                            <CreateCompanyUserCard companyUserId={id} />
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-4">
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                              <p>
                                Cartão personal
                              </p>

                              {companyUser?.cards[0]?.status === 'blocked' && (
                                <Badge
                                  Icon={React.Fragment}
                                  text='Bloqueado'
                                  valueDataTestid='blocked_card_badge'
                                />
                              )}
                            </div>

                            {companyUser?.cards[0]?.status === 'blocked' ? (
                              <UnblockPersonalCard userId={id} cardId={companyUser?.cards[0]?.id} />
                            ) : (
                              <BlockPersonalCard userId={id} cardId={companyUser?.cards[0]?.id} />
                            )}
                          </div>

                          <div className='grid grid-cols-2 gap-2'>
                            <ResumeBox
                              label='Status do cartão'
                              value={
                                companyUser?.cards[0]?.status
                                  ? cardStatusTranslation[companyUser?.cards[0]?.status as keyof typeof cardStatusTranslation]
                                  : '--'
                              }
                            />

                            <ResumeBox
                              label='Código'
                              value={(
                                <div className='flex items-center gap-2'>
                                  <p className='text- font-medium'>
                                    {companyUser?.cards[0]?.last_four_digits ? `**** **** **** ${companyUser?.cards[0]?.last_four_digits}` : ''}
                                  </p>

                                  <Button
                                    variant='link-white'
                                    name="copy_user_card"
                                    onClick={() => personalCard(companyUser?.cards[0].id)}
                                    isLoading={personalCardIsPending}
                                  >
                                    <CopyButton control={control} className="text-blue-800 size-4" />
                                  </Button>
                                </div>
                              )}
                            />
                          </div>

                          <ConsumptionLimit userId={companyUser?.id} card={companyUser?.cards[0]} />
                        </div>
                      )}
                    </div>

                    {(userHasPermissionToResetDriverAppPin && id) && (
                      <div className="w-full rounded-sm p-4 bg-white-100">
                        <div className="flex w-full items-center justify-between">
                          <p>Pin do App do Condutor</p>

                          <div className="flex items-center gap-2">

                            <ResetDriverAppPin id={id} />
                          </div>
                        </div>
                      </div>
                    )}

                    {(userHasPermissionToRequestPasswordReset && !!companyUser?.role_permits_client_user_password_reset_by_others) && (
                      <div className="w-full rounded-sm p-4 bg-white-100">
                        <div className="flex w-full items-center justify-between">
                          <p>Senha de acesso ao sistema de gestão TCard</p>

                          <div className="flex items-center gap-2">

                            <RequestPasswordReset id={id!} />
                          </div>
                        </div>
                      </div>
                    )}

                    {(userHasPermissionToShowConsumptionPassword || userHasPermissionToRequestConsumptionPasswordReset) && (
                      <div className="w-full rounded-sm p-4 bg-white-100">
                        <div className="flex w-full items-center justify-between">
                          <p>Senha para consumo com cartão</p>

                          <div className="flex items-center gap-2">
                            {userHasPermissionToShowConsumptionPassword && <ShowConsumptionPassword id={id!} />}

                            {userHasPermissionToRequestConsumptionPasswordReset && <RequestConsumptionPasswordReset id={id!} />}
                          </div>
                        </div>
                      </div>
                    )}

                    <ClientUserTokens />
                  </Box>
                </>
              )
            },
            {
              value: 'user-branches-access',
              label: 'Permissões nas filiais',
              hidden: company?.branch_hierarchy_for_user_access !== 'dynamic',
              operationKlassToCheckPermission: 'Admin::Client::UserBranchAccesses::Filter',
              body: (
                <UserBranchesAccesses />
              ),
            },
            {
              label: 'Histórico de consumo',
              value: 'consumptionHistory',
              body: (
                <ConsumptionHistory />
              )
            },
            {
              label: 'Histórico de ações',
              value: 'actionsHistory',
              body: (
                <LogRequestsTable type="ClientUser" id={id} />
              )
            },
          ]}
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default Show;
